import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ListChecks, ChevronRight, Loader2 } from 'lucide-react';
import { setupApi, queryKeys, type SetupOverviewItem } from '@/lib/api';

const STATUS_DOT: Record<string, string> = {
  done: 'bg-green-500',
  todo: 'bg-amber-400',
  blocked: 'bg-gray-300 dark:bg-gray-600',
};

function StepDots({ steps }: { steps: SetupOverviewItem['steps'] }) {
  return (
    <div className="flex items-center gap-1">
      {steps.map((s) => (
        <span
          key={s.key}
          title={`${s.label}: ${s.status}`}
          className={`h-2 w-2 rounded-full ${STATUS_DOT[s.status] ?? 'bg-gray-300'}`}
        />
      ))}
    </div>
  );
}

export default function OnboardingOverview() {
  const [showComplete, setShowComplete] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.setup.overview(),
    queryFn: () => setupApi.getOverview(),
    staleTime: 30000,
    refetchInterval: 60000,
  });

  const all = data?.facilities ?? [];
  // Incomplete first, then by least-complete, then name.
  const sorted = [...all].sort((a, b) => {
    if (a.complete !== b.complete) return a.complete ? 1 : -1;
    if (a.completed !== b.completed) return a.completed - b.completed;
    return a.name.localeCompare(b.name);
  });
  const visible = showComplete ? sorted : sorted.filter((f) => !f.complete);
  const completeCount = all.filter((f) => f.complete).length;

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
        <ListChecks className="h-4 w-4 text-blue-500" />
        <h2 className="text-sm font-semibold">Onboarding readiness</h2>
        <span className="text-xs text-gray-400">{completeCount}/{all.length} fully set up</span>
        <button
          onClick={() => setShowComplete((v) => !v)}
          className="ml-auto text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          {showComplete ? 'Hide complete' : 'Show all'}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : visible.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-gray-400">
          {all.length > 0 ? 'Every facility is fully onboarded. 🎉' : 'No facilities.'}
        </p>
      ) : (
        <div className="max-h-96 divide-y divide-gray-50 overflow-auto dark:divide-gray-800">
          {visible.map((f) => {
            const incomplete = f.steps.filter((s) => s.status !== 'done').map((s) => s.label);
            return (
              <Link
                key={f.facilityId}
                to={`/facilities/${f.id}/settings?tab=setup`}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                  f.complete
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                }`}>
                  {f.completed}/{f.total}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {f.name}
                    {!f.isActive && <span className="ml-2 text-xs font-normal text-gray-400">(inactive)</span>}
                  </p>
                  {incomplete.length > 0 && (
                    <p className="truncate text-xs text-gray-400">Needs: {incomplete.join(', ')}</p>
                  )}
                </div>
                <StepDots steps={f.steps} />
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
